import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

import type { AlertRule } from '../models/alert.types'
import type { ChannelAvailability } from '../models/channel.types'
import type { AlertHistoryRow } from '../models/history.types'
import {
  awaitAlertReportCatalog,
  useAlertLevelMetadata,
  useAlertMetricCatalog,
} from '../models/reports-bridge'
import {
  alertService,
  type AlertBundle,
  type AlertChannelLoader,
  type AlertProjection,
  type AlertUnavailableSlice,
  type AlertWorkspaceId,
} from '../service/alertService'

import { ApiError } from '@/shared/http'
import { useFormat } from '@/shared/lib/format'

export type AlertsLoadStatus = 'idle' | 'loading' | 'error' | 'ready'

export interface AlertsLoadOptions {
  notificationTypeId: number
  loadChannels: AlertChannelLoader
}

interface AlertsLoadRequest {
  workspaceIds: readonly AlertWorkspaceId[]
  options: AlertsLoadOptions
}

export const useAlertsStore = defineStore('alerts', () => {
  const bundle = shallowRef<AlertBundle | null>(null)
  const activeOverrides = ref(new Map<number, boolean>())
  const removedIds = ref(new Set<number>())

  const projection = computed<AlertProjection>(() => {
    const current = bundle.value
    if (!current) return { rules: [], history: [] }
    return current.project ? current.project() : current
  })
  const rules = computed<AlertRule[]>(() =>
    projection.value.rules
      .filter((rule) => !removedIds.value.has(rule.id))
      .map((rule) => {
        const active = activeOverrides.value.get(rule.id)
        return active === undefined || active === rule.isActive
          ? rule
          : { ...rule, isActive: active }
      }),
  )
  const history = computed<AlertHistoryRow[]>(() => projection.value.history)
  const channels = ref<ChannelAvailability[]>([])
  const unavailableSlices = ref<AlertUnavailableSlice[]>([])
  const status = ref<AlertsLoadStatus>('idle')
  const error = ref<ApiError | null>(null)
  const togglingId = ref<number | null>(null)
  let generation = 0
  let lastRequest: AlertsLoadRequest | null = null

  const hasAlerts = computed(() => rules.value.length > 0)
  const activeCount = computed(() => rules.value.filter((rule) => rule.isActive).length)
  const triggeredCount = computed(() =>
    rules.value.reduce((total, rule) => total + rule.triggerCount, 0),
  )
  const isEmpty = computed(() => status.value === 'ready' && rules.value.length === 0)

  function rulesByName(query: string): AlertRule[] {
    const normalized = query.trim().toLowerCase()
    return normalized
      ? rules.value.filter((rule) => rule.name.toLowerCase().includes(normalized))
      : [...rules.value]
  }

  function historyByName(query: string): AlertHistoryRow[] {
    const normalized = query.trim().toLowerCase()
    return normalized
      ? history.value.filter((row) => row.name.toLowerCase().includes(normalized))
      : [...history.value]
  }

  async function load(
    workspaceIds: readonly AlertWorkspaceId[],
    options: AlertsLoadOptions,
  ): Promise<AlertBundle | null> {
    const loadGeneration = ++generation
    lastRequest = { workspaceIds: [...workspaceIds], options }
    status.value = 'loading'
    error.value = null
    clearBundle()
    channels.value = []
    unavailableSlices.value = []

    const catalog = useAlertMetricCatalog()
    const levels = useAlertLevelMetadata()
    const formatter = useFormat()
    try {
      const loaded = await alertService.loadAll(workspaceIds, {
        notificationTypeId: options.notificationTypeId,
        loadChannels: options.loadChannels,
        awaitCatalog: awaitAlertReportCatalog,
        metricCatalog: () => catalog,
        levelMetadata: () => levels,
        formatDate: (value) => formatter.formatDate(value),
      })
      if (loadGeneration !== generation) return null
      bundle.value = loaded
      channels.value = loaded.channels
      unavailableSlices.value = loaded.unavailableSlices
      status.value = 'ready'
      return loaded
    } catch (cause) {
      if (loadGeneration !== generation) return null
      error.value = toApiError(cause)
      status.value = 'error'
      return null
    }
  }

  async function toggleActive(rule: AlertRule): Promise<void> {
    if (togglingId.value !== null) return
    const current = rules.value.find((item) => item.id === rule.id)
    if (!current) return
    const previous = current.isActive
    const next = !previous
    togglingId.value = current.id
    setActiveOverride(current.id, next)
    try {
      await alertService.setActive(current.id, next)
    } catch (cause) {
      setActiveOverride(current.id, previous)
      throw toApiError(cause)
    } finally {
      if (togglingId.value === current.id) togglingId.value = null
    }
  }

  async function remove(rule: AlertRule): Promise<void> {
    try {
      await alertService.remove(rule.id)
      removedIds.value = new Set(removedIds.value).add(rule.id)
    } catch (cause) {
      throw toApiError(cause)
    }
  }

  async function upsertFromSave(): Promise<AlertBundle | null> {
    if (!lastRequest) return null
    return load(lastRequest.workspaceIds, lastRequest.options)
  }

  function reset(): void {
    generation += 1
    lastRequest = null
    clearBundle()
    channels.value = []
    unavailableSlices.value = []
    status.value = 'idle'
    error.value = null
    togglingId.value = null
  }

  function setActiveOverride(id: number, active: boolean): void {
    activeOverrides.value = new Map(activeOverrides.value).set(id, active)
  }

  function clearBundle(): void {
    bundle.value = null
    activeOverrides.value = new Map()
    removedIds.value = new Set()
  }

  function toApiError(cause: unknown): ApiError {
    return ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
  }

  return {
    rules,
    history,
    channels,
    unavailableSlices,
    status,
    error,
    togglingId,
    hasAlerts,
    activeCount,
    triggeredCount,
    isEmpty,
    rulesByName,
    historyByName,
    load,
    toggleActive,
    remove,
    upsertFromSave,
    reset,
  }
})

export type AlertsStore = ReturnType<typeof useAlertsStore>
